import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { styled } from '@xstyled/styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import {
  ModalWrapper,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton
} from '~/editor/v2/cards/edit-modal/sharedComponents';
import CloseIcon from '~/assets/icons/close.svg';
import { CardTemplate } from './templates/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: CardTemplate) => void;
  cardTemplates: CardTemplate[];
}

const TypeGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 27px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: space-around;
  }
`;

const TypeCard = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 160px;

  &:focus {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
`;

const CardImageWrapper = styled.div`
  width: 160px;
  height: 224px;
  overflow: hidden;
  position: relative;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  box-shadow:
    0 -1.194px 2.388px 0 rgba(0, 0, 0, 0.05),
    0 17.91px 17.91px 0 rgba(0, 0, 0, 0.03),
    0 8.955px 11.94px 0 rgba(0, 0, 0, 0.05);
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const CardLabel = styled.div`
  font-family: ralewayBold;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  color: #000;
  text-align: center;
  padding: 16px;
  background: white;
`;

export const TypeSelectionModal: React.FC<Props> = ({
  open,
  onClose,
  onSelectTemplate,
  cardTemplates
}) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  const handleSelectTemplate = (template: CardTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  if (!open) {
    return null;
  }

  const modalContent = (
    <Overlay open={true} opacity={0.5} onClick={onClose}>
      <ModalWrapper onClick={e => e.stopPropagation()}>
        <Modal
          open={true}
          width="616px"
          horizontal="center"
          vertical="center"
          borderRadius="20px"
        >
          <ModalContent>
            <ModalHeader>
              <Title>Select Card Type</Title>
              <CloseButton
                onClick={onClose}
                aria-label="Close select card type modal"
              >
                <CloseIcon />
              </CloseButton>
            </ModalHeader>

            <TypeGrid>
              {cardTemplates.map(template => (
                <TypeCard
                  key={template.type}
                  onClick={() => handleSelectTemplate(template)}
                  aria-label={template.label}
                >
                  <CardImageWrapper>
                    <CardImage src={template.cardImage} alt={template.label} />
                  </CardImageWrapper>
                  <CardLabel>{template.label}</CardLabel>
                </TypeCard>
              ))}
            </TypeGrid>
          </ModalContent>
        </Modal>
      </ModalWrapper>
    </Overlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
